import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getArticleBySlug, getArticlesByCategory, getLatestArticles } from '@/mocks/articles';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { ArticleLD, WebPageLD } from '@/components/feature/JsonLD';
import type { Article } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const article: Article | undefined = slug ? getArticleBySlug(slug) : undefined;

  useEffect(() => {
    if (article) {
      document.title = `${article.title} - 萌白酱Official`;
    }
  }, [article]);

  if (!article) {
    return (
      <main className="pt-16 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-2xl bg-background-100 text-foreground-400 mb-4">
            <i className="ri-file-unknow-line text-3xl"></i>
          </div>
          <h2 className="text-xl font-heading text-foreground-900 mb-2">文章未找到</h2>
          <p className="text-sm text-foreground-500 mb-6">该文章可能已被删除或链接无效</p>
          <Link
            to="/"
            className="inline-flex items-center px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors duration-200 cursor-pointer"
          >
            <i className="ri-home-line mr-2"></i>返回首页
          </Link>
        </div>
      </main>
    );
  }

  const relatedArticles = getArticlesByCategory(article.categorySlug)
    .filter((a) => a.id !== article.id)
    .slice(0, 3);

  const articleUrl = `https://mengbaijiang.com/article/${article.slug}`;

  return (
    <>
      <ArticleLD
        headline={article.title}
        description={article.excerpt}
        url={articleUrl}
        image={article.coverImage}
        datePublished={article.publishDate}
        dateModified={article.publishDate}
        authorName={article.author}
        authorUrl="https://mengbaijiang.com/about"
      />
      <WebPageLD
        url={articleUrl}
        name={article.title}
        description={article.excerpt}
        breadcrumb={[
          { name: '首页', url: 'https://mengbaijiang.com' },
          { name: article.categoryName, url: `https://mengbaijiang.com/category/${article.categorySlug}` },
          { name: article.title, url: articleUrl },
        ]}
      />

      <main className="pt-16">
        <article>
          <div className="relative w-full h-[300px] md:h-[450px] overflow-hidden">
            <img
              src={article.coverImage}
              alt={article.title}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: article.categoryName, href: `/category/${article.categorySlug}` },
                  { label: article.title },
                ]}
              />
            </div>
          </div>

          <div className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
            <div className="max-w-3xl mx-auto">
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <span className="px-3 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full">
                  {article.categoryName}
                </span>
                {article.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 bg-secondary-100 text-secondary-800 text-xs font-medium rounded-full">
                    {tag}
                  </span>
                ))}
              </div>

              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading text-foreground-900 mb-4 leading-tight">
                {article.title}
              </h1>

              <div className="flex items-center gap-4 text-sm text-foreground-500 mb-8 pb-8 border-b border-background-200/70">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                    <i className="ri-user-3-line text-sm"></i>
                  </div>
                  <span className="font-medium text-foreground-800">{article.author}</span>
                </div>
                <span>·</span>
                <span><i className="ri-calendar-line mr-1"></i>{article.publishDate}</span>
                <span>·</span>
                <span><i className="ri-eye-line mr-1"></i>{(article.views / 10000).toFixed(1)}万 阅读</span>
                <span>·</span>
                <span><i className="ri-heart-line mr-1"></i>{(article.likes / 10000).toFixed(1)}万 点赞</span>
              </div>

              <div
                className="prose prose-sm md:prose-base max-w-none
                  prose-headings:font-heading prose-headings:text-foreground-900
                  prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:my-4
                  prose-img:rounded-2xl
                  prose-a:text-primary-500 prose-a:no-underline hover:prose-a:text-primary-600
                  prose-strong:text-foreground-900
                  prose-li:text-foreground-700
                  prose-h3:text-lg md:prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-4"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              <div className="mt-10 pt-8 border-t border-background-200/70">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-foreground-500">分享：</span>
                    {[
                      { icon: 'ri-weibo-line', label: '微博' },
                      { icon: 'ri-qq-line', label: 'QQ' },
                      { icon: 'ri-wechat-line', label: '微信' },
                      { icon: 'ri-link', label: '复制链接' },
                    ].map((item) => (
                      <button
                        key={item.label}
                        className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 hover:bg-primary-100 hover:text-primary-600 transition-all duration-200 cursor-pointer"
                        aria-label={item.label}
                      >
                        <i className={`${item.icon} text-sm`}></i>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {relatedArticles.length > 0 && (
            <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-100">
              <div className="max-w-6xl mx-auto">
                <h2 className="text-xl md:text-2xl font-heading text-foreground-900 mb-8">
                  <i className="ri-article-line text-primary-500 mr-2"></i>
                  相关推荐
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {relatedArticles.map((related: Article) => (
                    <Link
                      key={related.id}
                      to={`/article/${related.slug}`}
                      className="group bg-background-50 border border-background-200/70 rounded-2xl overflow-hidden hover:border-primary-200 transition-all duration-300 cursor-pointer"
                    >
                      <div className="aspect-[16/10] overflow-hidden">
                        <img
                          src={related.coverImage}
                          alt={related.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-4">
                        <h3 className="text-sm font-heading text-foreground-900 line-clamp-2 group-hover:text-primary-500 transition-colors duration-200">
                          {related.title}
                        </h3>
                        <div className="flex items-center gap-3 mt-2 text-xs text-foreground-400">
                          <span>{related.publishDate}</span>
                          <span>{(related.views / 10000).toFixed(1)}万阅读</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}
        </article>
      </main>
    </>
  );
}